import { createSlice } from "@reduxjs/toolkit";
import {
  createTicket,
  getMyTickets,
  getAllTickets,
  getTicketStats,
  getTicketById,
  replyToTicket,
  updateTicketStatus,
  updateTicketPriority,
  assignTicket,
} from "./ThunkActions";
import { ITicketsState, REDUCER_NAME } from "./Types";

const initialState: ITicketsState = {
  isLoading: false,
  myTickets: [],
  myTicketsTotal: 0,
  allTickets: [],
  allTicketsTotal: 0,
  stats: null,
  activeTicket: null,
  activeTicketMessages: [],
  errorMessage: null,
  replyLoading: false,
};

const ticketsSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: {
    resetTicketsState: () => initialState,
    clearActiveTicket: (state) => {
      state.activeTicket = null;
      state.activeTicketMessages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTicket.pending, (state) => { state.isLoading = true; state.errorMessage = null; })
      .addCase(createTicket.fulfilled, (state, action) => {
        const ticket = (action.payload as any)?.data?.data;
        if (ticket) state.myTickets = [ticket, ...state.myTickets];
        state.isLoading = false;
      })
      .addCase(createTicket.rejected, (state) => { state.isLoading = false; state.errorMessage = "Failed to create ticket"; })

      .addCase(getMyTickets.pending, (state) => { state.isLoading = true; state.errorMessage = null; })
      .addCase(getMyTickets.fulfilled, (state, action) => {
        const d = (action.payload as any)?.data?.data;
        state.myTickets = d?.tickets || [];
        state.myTicketsTotal = d?.total || 0;
        state.isLoading = false;
      })
      .addCase(getMyTickets.rejected, (state) => { state.isLoading = false; state.errorMessage = "Failed to fetch your tickets"; })

      .addCase(getAllTickets.pending, (state) => { state.isLoading = true; state.errorMessage = null; })
      .addCase(getAllTickets.fulfilled, (state, action) => {
        const d = (action.payload as any)?.data?.data;
        state.allTickets = d?.tickets || [];
        state.allTicketsTotal = d?.total || 0;
        state.isLoading = false;
      })
      .addCase(getAllTickets.rejected, (state) => { state.isLoading = false; state.errorMessage = "Failed to fetch tickets"; })

      .addCase(getTicketStats.fulfilled, (state, action) => {
        state.stats = (action.payload as any)?.data?.data || null;
      })

      .addCase(getTicketById.pending, (state) => { state.isLoading = true; state.errorMessage = null; })
      .addCase(getTicketById.fulfilled, (state, action) => {
        const d = (action.payload as any)?.data?.data;
        state.activeTicket = d?.ticket || null;
        state.activeTicketMessages = d?.messages || [];
        state.isLoading = false;
      })
      .addCase(getTicketById.rejected, (state) => { state.isLoading = false; state.errorMessage = "Failed to fetch ticket"; })

      .addCase(replyToTicket.pending, (state) => { state.replyLoading = true; })
      .addCase(replyToTicket.fulfilled, (state, action) => {
        const ticket = (action.payload as any)?.data?.data;
        if (ticket) state.activeTicket = ticket;
        state.replyLoading = false;
      })
      .addCase(replyToTicket.rejected, (state) => { state.replyLoading = false; })

      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        const ticket = (action.payload as any)?.data?.data;
        if (ticket) {
          state.activeTicket = ticket;
          state.allTickets = state.allTickets.map((t) => (t.id === ticket.id ? ticket : t));
        }
      })

      .addCase(updateTicketPriority.fulfilled, (state, action) => {
        const ticket = (action.payload as any)?.data?.data;
        if (ticket) {
          state.activeTicket = ticket;
          state.allTickets = state.allTickets.map((t) => (t.id === ticket.id ? ticket : t));
        }
      })

      .addCase(assignTicket.fulfilled, (state, action) => {
        const ticket = (action.payload as any)?.data?.data;
        if (ticket) {
          state.activeTicket = ticket;
          state.allTickets = state.allTickets.map((t) => (t.id === ticket.id ? ticket : t));
        }
      });
  },
});

export const { resetTicketsState, clearActiveTicket } = ticketsSlice.actions;
export default ticketsSlice.reducer;
